import CalendarEntry from 'owa-service/lib/contract/CalendarEntry';
import getStore from '../store/store';
import LinkedCalendarEntry from 'owa-service/lib/contract/LinkedCalendarEntry';
import LocalCacheForRemoteCalendarEntry from 'owa-service/lib/contract/LocalCacheForRemoteCalendarEntry';
import LocalCalendarEntry from 'owa-service/lib/contract/LocalCalendarEntry';
import {
    getFolderIdByCalendarID,
    getCalendarIdByFolderId,
    getCalendarEntryByCalendarId,
} from './calendarsCacheSelectors';

export function getCalendarEntryByFolderId(folderId: string): CalendarEntry | undefined {
    const calendarId = getCalendarIdByFolderId(folderId);

    // If the calendar ID is present then get the CalendarEntry for it otherwise return undefined
    return calendarId ? getCalendarEntryByCalendarId(calendarId.id) : undefined;
}

export function calendarIsInCache(calendarId: string): boolean {
    return getCalendarEntryByCalendarId(calendarId) ? true : false;
}

/**
 * Returns the CalendarFolder for a given FolderId
 * @param folderId FolderId of the calendar folder
 */
export function getCalendarFolderByFolderId(folderId: string): CalendarEntry | undefined {
    const { folderIdToCalendarId, calendarFolderMapping } = getStore();
    const calendarId = folderIdToCalendarId.get(folderId);

    // If the calendar ID is present then get the CalendarFolder for it otherwise return undefined
    return calendarId ? calendarFolderMapping.get(calendarId.id) : undefined;
}

/**
 * Returns whether this folder is a LocalCalendarEntry or a LocalCacheForRemoteCalendarEntry
 * that has a valid CalendarFolderId
 * @param folderId FolderId of the calendar folder
 */
export function isLocalCalendarEntry(folderId: string): boolean {
    const { folderIdToCalendarId, calendarEntryMapping } = getStore();

    const calendarId = folderIdToCalendarId.get(folderId);
    if (calendarId) {
        var calendarEntry: any = calendarEntryMapping.get(calendarId.id);

        // Group mailboxes are Linked calendar entries
        return !calendarEntry.IsGroupMailboxCalendar && calendarEntry.CalendarFolderId != undefined;
    } else {
        return false;
    }
}

/**
 * Returns whether the given folder is a readonly local calendar entry
 * @param folderId The folder id
 * @returns True if it's a read only local calendar entry, false otherwise
 */
export function isReadOnlyLocalCalendar(folderId: string): boolean {
    if (isLocalCalendarEntry(folderId)) {
        let calendarEntry: LocalCalendarEntry = getCalendarEntryByFolderId(folderId);
        return calendarEntry.IsReadOnly;
    }
    return false;
}

/**
 * Checks whether the calendarEntry has been marked as an invalid folder
 * @param calendarEntry the calendar folder to check for validity
 */
export function isValidCalendarEntry(calendarEntry: CalendarEntry) {
    const { validEntryMapping } = getStore();

    return calendarEntry && validEntryMapping.get(calendarEntry.ItemId.Id);
}

/**
 * Checks if the calendar is a LocalCacheForRemoteCalendarEntry calendar with an
 * invalid folder id. When the cache is initialized LocalCacheForRemoteCalendarEntry
 * calendar entries do not have accurate folder ids. They should be fetched/ populated as needed.
 */
export function calendarFolderIdNeedsUpdate(calendarId: string): boolean {
    const folderId = getFolderIdByCalendarID(calendarId);
    if (!isLocalCalendarEntry(folderId)) {
        const calendarEntry: LocalCacheForRemoteCalendarEntry = getCalendarEntryByCalendarId(
            calendarId
        ) as LocalCacheForRemoteCalendarEntry;
        return !isValidCalendarEntry(calendarEntry);
    }
    return false;
}

export function getCalendarEntryByEmailAddress(emailAddress: string): CalendarEntry | undefined {
    const { folderIdToCalendarId, calendarEntryMapping } = getStore();
    const calendarId = folderIdToCalendarId.get(emailAddress);
    let calendarEntry;

    // If the calendar ID is present then get the CalendarEntry for it,
    // otherwise search through calendar entries for a LocalCacheForRemoteCalendarEntry
    if (calendarId) {
        calendarEntry = calendarEntryMapping.get(calendarId.id);
    } else {
        let entries = [...calendarEntryMapping.values()] as (
            | LinkedCalendarEntry
            | LocalCacheForRemoteCalendarEntry)[];

        calendarEntry = entries.filter(entry => {
            return (
                entry.OwnerEmailAddress &&
                entry.OwnerEmailAddress.toLowerCase() == emailAddress.toLowerCase()
            );
        })[0];
    }

    return calendarEntry;
}
